import type { ActionSourceCategory } from "./actionSourceSegments";

/**
 * 출처 범주의 표시 이름·설명·색.
 *
 * 이름은 데이터 어휘(`ActionSource` enum)를 그대로 쓴다. 화면에서 본 이름을 parquet·MCP 응답과
 * 그대로 대조하기 위해서다.
 *
 * 색은 판정이 아니다. status 톤(warning·error)을 쓰지 않고 데이터 구분색(`dataViz`)만 쓴다.
 * 다수인 INFERENCE 는 회색 바탕으로 두어, 사람이 낸 구간이 눈에 띈다.
 */

export type ActionSourceDataVizKey = "gray" | "orange" | "violet" | "green" | "red";

export type ActionSourceTerm = {
  label: string;
  // 이름 대신 표시하는 대문자 약칭. 데이터 어휘의 앞 세 글자다. 이름이 한국어인 범주는 그
  // 뜻의 영어 어휘를 줄인다. 세 글자면 여섯 범주가 모두 구분되고, 색이 한 번 더 구분한다.
  abbr: string;
  tip: string;
  dataViz: ActionSourceDataVizKey;
};

const TERMS: Record<Exclude<ActionSourceCategory, "unknown">, ActionSourceTerm> = {
  INFERENCE: {
    label: "INFERENCE",
    abbr: "INF",
    tip: "Policy가 스스로 낸 Action이 기록된 구간입니다.",
    dataViz: "gray",
  },
  INTERVENTION: {
    label: "INTERVENTION",
    abbr: "INT",
    tip: "Policy가 도는 중에 사람이 끼어들어 낸 Action이 기록된 구간입니다.",
    dataViz: "orange",
  },
  TELEOP: {
    label: "TELEOP",
    abbr: "TEL",
    tip: "사람이 원격 조작으로 낸 Action이 기록된 구간입니다.",
    dataViz: "violet",
  },
  SCRIPT: {
    label: "SCRIPT",
    abbr: "SCR",
    tip: "미리 짠 스크립트가 낸 Action이 기록된 구간입니다.",
    dataViz: "green",
  },
  invalid: {
    label: "무효 값",
    abbr: "INV",
    tip: "NaN이나 정수가 아닌 값이 기록된 구간입니다.",
    dataViz: "red",
  },
};

/** 범주의 표시 이름·설명·색. 정의 밖 값은 기록된 정수를 이름에 넣는다. */
export function actionSourceTerm(
  category: ActionSourceCategory,
  code: number | null = null,
): ActionSourceTerm {
  if (category === "unknown") {
    const value = code === null ? "?" : String(code);
    return {
      label: `정의 밖 값 ${value}`,
      abbr: "UNK",
      tip: `ActionSource 어휘에 없는 값(${value})이 기록된 구간입니다.`,
      dataViz: "red",
    };
  }
  return TERMS[category];
}

// 리터럴로 적는다. `styles.test.ts` 는 소스의 `var(--c6-*)` 를 c6 가 실제로 내보내는 이름과
// 대조한다. 템플릿 문자열로 조립하면 그 검사가 이름을 찾지 못한다.
const DOT_COLOR: Record<ActionSourceDataVizKey, string> = {
  gray: "var(--c6-color-data-viz-gray)",
  orange: "var(--c6-color-data-viz-orange)",
  violet: "var(--c6-color-data-viz-violet)",
  green: "var(--c6-color-data-viz-green)",
  red: "var(--c6-color-data-viz-red)",
};

/** 범례 점의 색. c6 가 테마별로 내보내는 dataViz CSS 변수다. */
export function actionSourceDotColor(key: ActionSourceDataVizKey): string {
  return DOT_COLOR[key];
}
